import { useState, useMemo } from 'react';
import {
  useSessionAttendance,
  useMarkAttendance,
  useMarkAllPresent,
  useCancelMakeup,
} from '@/hooks/use-attendance';
import { EvaluationModal } from '@/components/coach/evaluation-modal';
import { BulkEvaluationWizard } from '@/components/coach/bulk-evaluation-wizard';
import type { Player } from '@/shared/types/coach.types';
import { SessionNotes } from '@/components/coach/session-notes';
import { SessionEvaluationsSummary } from '@/components/coach/session-evaluations-summary';
import { useSessionEvaluations } from '@/hooks/use-evaluation';
import { GroupAttendanceList } from './group-attendance-list';
import { GroupSessionActions } from './group-session-actions';
import { AddPlayersSheet } from './add-players-sheet';

interface GroupSessionViewProps {
  sessionId: string;
}

export function GroupSessionView({ sessionId }: GroupSessionViewProps) {
  const { data } = useSessionAttendance(sessionId);
  const { data: sessionEvaluations } = useSessionEvaluations(sessionId);
  const markAttendance = useMarkAttendance(sessionId);
  const markAllPresent = useMarkAllPresent(sessionId);
  const cancelMakeup = useCancelMakeup(sessionId);

  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [evalModalOpen, setEvalModalOpen] = useState(false);
  const [bulkEvalOpen, setBulkEvalOpen] = useState(false);
  const [addSheetOpen, setAddSheetOpen] = useState(false);

  const playersToEvaluate = useMemo(() => {
    if (!data) return [];
    return data.players
      .filter(p => (p.status === 'present' || p.status === 'late') && !p.hasEvaluation)
      .map(p => ({
        id: p.id,
        firstName: p.firstName,
        lastName: p.lastName,
        level: p.level,
      }));
  }, [data]);

  const handleOpenEvaluation = (player: {
    id: string;
    firstName: string;
    lastName: string;
    level: string | null;
  }, e: React.MouseEvent) => {
    e.stopPropagation();
    const skillLevel = (player.level || 'beginner') as 'beginner' | 'intermediate' | 'advanced' | 'elite';
    setSelectedPlayer({
      id: player.id,
      firstName: player.firstName,
      lastName: player.lastName,
      dateOfBirth: '',
      skillLevel,
      ballLevel: null,
      utrRating: null,
      photoUrl: null,
    });
    setEvalModalOpen(true);
  };

  const handleToggleAttendance = (playerId: string, currentStatus: string) => {
    let newStatus: 'present' | 'late' | 'absent';
    if (currentStatus === 'present') {
      newStatus = 'late';
    } else if (currentStatus === 'late') {
      newStatus = 'absent';
    } else {
      newStatus = 'present';
    }
    markAttendance.mutate({ playerId, status: newStatus });
  };

  const handleRequestMakeup = (playerId: string, hasMakeupRequest: boolean, e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasMakeupRequest) {
      cancelMakeup.mutate({ playerId });
    } else {
      markAttendance.mutate({ playerId, status: 'absent', makeupRequired: true });
    }
  };

  const handleMarkAllPresent = () => {
    if (!data) return;
    const notPresentIds = data.players
      .filter(p => p.status !== 'present')
      .map(p => p.id);
    if (notPresentIds.length > 0) {
      markAllPresent.mutate(notPresentIds);
    }
  };

  if (!data) return null;

  return (
    <>
      <GroupAttendanceList
        players={data.players}
        onToggleAttendance={handleToggleAttendance}
        onOpenEvaluation={handleOpenEvaluation}
        onRequestMakeup={handleRequestMakeup}
        onAddPlayers={() => setAddSheetOpen(true)}
      />

      <SessionEvaluationsSummary
        sessionId={sessionId}
        presentCount={data.presentCount}
      />

      <SessionNotes sessionId={sessionId} />

      <GroupSessionActions
        hasPlayers={data.players.length > 0}
        presentCount={data.presentCount}
        totalCount={data.totalCount}
        isMarkingAll={markAllPresent.isPending}
        playersToEvaluateCount={playersToEvaluate.length}
        onMarkAllPresent={handleMarkAllPresent}
        onStartBulkEval={() => setBulkEvalOpen(true)}
      />

      <AddPlayersSheet
        sessionId={sessionId}
        open={addSheetOpen}
        onOpenChange={setAddSheetOpen}
      />

      <EvaluationModal
        player={selectedPlayer}
        sessionId={sessionId}
        open={evalModalOpen}
        onOpenChange={setEvalModalOpen}
        onSuccess={() => {}}
        initialEvaluation={(sessionEvaluations ?? []).find(e => e.playerId === selectedPlayer?.id)}
      />

      <BulkEvaluationWizard
        sessionId={sessionId}
        players={playersToEvaluate}
        open={bulkEvalOpen}
        onOpenChange={setBulkEvalOpen}
        onComplete={() => {}}
      />
    </>
  );
}
